import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMobileDetect } from "../../hooks";
import { useAuthStore } from "../../store/useAuthStore";

import {
    Box,
    Paper,
    BottomNavigation,
    BottomNavigationAction,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import { ProfileMenu } from "../ui/ProfileMenu";

import logoWizard from "../../_assets/logo_wizard.png";
import logoDefault from "../../_assets/logo.png";
import logoFavela from "../../_assets/logo_favela.png";

import { buildMenuItems } from "../../data/MenusItens";

const logos = {
    wizard: logoWizard,
    favela: logoFavela,
    default: logoDefault,
};

export const Menu = ({ client = "default", creatorIsAdmin, comunidade }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const isMobile = useMobileDetect();
    const creator = useAuthStore((state) => state.creator);

    const [openProfileMenu, setOpenProfileMenu] = useState(false);

    const logo = logos[client] || logos.default;

    const filteredMenuItems = buildMenuItems({ creatorIsAdmin, isMobile, comunidade });

    const selected = filteredMenuItems.findIndex((item => {
        if (item.link) {
            return location.pathname.startsWith(item.link);
        }
        return false;
    }));

    const handleNavigation = (index, item) => {
        if (item.link) {
            navigate(item.link);
        }
    };

    const handleLogoClick = () => {
        if (client === "wizard" && comunidade) {
            navigate(`/comunidade/${comunidade}`);
        } else {
            navigate("/");
        }
    };

    const toggleProfileMenu = () => {
        setOpenProfileMenu(!openProfileMenu);
    };

    if (isMobile) {
        return (
            <Paper
                sx={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: "var(--menu-background-color)",
                }}
                elevation={3}
            >
                <BottomNavigation
                    showLabels
                    value={selected}
                    sx={{
                        color: "var(--menu-text-color) !important",
                        backgroundColor: "var(--menu-background-color)",
                        stroke: "var(--menu-text-color)",
                        "& .MuiBottomNavigationAction-root": {
                            "&:hover": {
                                backgroundColor: "var(--menu-selected-background-color)",
                            },
                            "&.Mui-selected": {
                                backgroundColor: "var(--menu-selected-background-color)",
                                color: "var(--menu-text-color)",
                            },
                            "& .MuiSvgIcon-root": {
                                color: "var(--menu-text-color) !important",
                            },
                        },
                    }}
                >
                    {filteredMenuItems.map((item, index) => {
                        if (item.showMobile === false) {
                            return null;
                        };

                        const isSelected = selected === index;
                        const Icon = isSelected ? item?.activeIcon : item?.icon;

                        return (
                            <BottomNavigationAction
                                key={index}
                                label={item.title}
                                icon={<Icon />}
                                color="var(--menu-text-color) !important"
                                 sx={{
                                    marginLeft: "auto",
                                    "&:hover": {
                                        backgroundColor: "var(--menu-selected-background-color)",
                                    },
                                    "& .Mui-selected": {
                                        backgroundColor: "var(--menu-selected-background-color)",
                                    },
                                    "& .MuiBottomNavigationAction-label": {
                                        color: "var(--menu-text-color) !important",
                                    },
                                 }}
                                onClick={() => handleNavigation(index, item)}
                            />
                        );
                    })}
                    <BottomNavigationAction
                        key="settings"
                        label="Configurações"
                        icon={<Settings />}
                        onClick={toggleProfileMenu}
                        sx={{
                            marginLeft: "auto",
                            color: "var(--menu-text-color) !important",
                            "&:hover": {
                                backgroundColor: "var(--menu-selected-background-color)",
                            },
                            "& .Mui-selected": {
                                backgroundColor: "var(--menu-selected-background-color)",
                            },
                            "& .MuiSvgIcon-root": {
                                color: "var(--menu-text-color) !important",
                            },
                        }}
                    />
                </BottomNavigation>
                <ProfileMenu
                    openDrawer={openProfileMenu}
                    toggleDrawer={toggleProfileMenu}
                    creator={creator}
                    comunidade={comunidade}
                />
            </Paper>
        );
    }

    return (
        <Box
            sx={{
                width: 240,
                height: "100vh",
                backgroundColor: "var(--menu-background-color)",
                color: "var(--white-color)",
                display: "flex",
                flexDirection: "column",
                position: "fixed",
                left: 0,
                top: 0,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "12px",
                    cursor: "pointer",
                }}
                onClick={handleLogoClick}
            >
                <img
                    src={logo}
                    alt={`${client} logo`}
                    style={{
                        maxWidth: "80%",
                        maxHeight: "50px",
                        objectFit: "contain",
                    }}
                />
            </Box>

            <Divider sx={{ backgroundColor: "rgba(255, 255, 255, 0.12)" }} />

            <List>
                {filteredMenuItems.map((item, index) => (
                    <ListItem
                        button
                        key={index}
                        selected={selected === index}
                        onClick={() => handleNavigation(index, item)}
                        sx={{
                            color: "var(--menu-text-color)",
                            '&:hover': {
                                backgroundColor: "var(--menu-selected-background-color)",
                            },
                            "&.Mui-selected": {
                                backgroundColor: "var(--menu-selected-background-color)",
                                color: "var(--menu-text-color)",
                                '&:hover': {
                                    backgroundColor: "var(--menu-selected-background-color)",
                                },
                            },
                        }}
                    >
                        <ListItemIcon sx={{ color: "inherit" }}>
                            <item.icon />
                        </ListItemIcon>
                        <ListItemText primary={item.title} />
                    </ListItem>
                ))}
            </List>

            <Box sx={{ mt: "auto" }}>
                <ListItem
                    onClick={toggleProfileMenu}
                    sx={{
                        borderTop: "1px solid rgba(255, 255, 255, 0.12)",
                        color: "var(--menu-text-color)",
                        cursor: "pointer",
                    }}
                >
                    <ListItemIcon >
                        <Settings sx={{ color: "var(--menu-text-color) !important" }} />
                    </ListItemIcon>
                    <ListItemText primary="Configurações" />
                </ListItem>
            </Box>

            <ProfileMenu
                openDrawer={openProfileMenu}
                toggleDrawer={toggleProfileMenu}
                creator={creator}
                comunidade={comunidade}
            />
        </Box>
    );
};
